import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, limit, Timestamp } from 'firebase/firestore';
import { GiRoundStar } from "react-icons/gi";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  tags?: string[];
  isDraft?: boolean;
  likes?: number;
}

const RecentBlogPosts = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecentPosts = async () => {
      try {
        // 新着順で多めに取得し、下書きを除いて3件に絞る
        const q = query(collection(db, 'blog'), orderBy('createdAt', 'desc'), limit(10));
        const snapshot = await getDocs(q);
        const allPosts = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title || '',
            content: data.content || '',
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt,
            tags: (data.tags as string[] | undefined) || [],
            isDraft: data.isDraft || false,
            likes: data.likes || 0,
          } as BlogPost;
        });
        // 下書きを除外して最大3件
        const publicPosts = allPosts.filter(p => !p.isDraft).slice(0, 3);
        setPosts(publicPosts);
      } catch (err) {
        console.error('RecentBlogPosts fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentPosts();
  }, []);

  // HTMLコンテンツからプレーンテキストを抽出して冒頭44文字を返す
  const getExcerpt = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length > 44 ? text.slice(0, 44) + '…' : text;
  };

  return (
    <div className="recent-blog-section">
      <div className="recent-blog-grid">
        {loading ? (
          <p className="recent-blog-loading">読み込み中…</p>
        ) : posts.length === 0 ? (
          <p className="recent-blog-loading">記事がありません</p>
        ) : (
          posts.map(post => (
            <article
              key={post.id}
              className="recent-blog-card"
              onClick={() => navigate(`/blog/${post.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && navigate(`/blog/${post.id}`)}
            >
              {/* タイトル */}
              <h3 className="recent-blog-card-title">
                <GiRoundStar style={{ marginRight: '6px', flexShrink: 0 }} />
                {post.title}
              </h3>

              {/* 本文抜粋 */}
              <p className="recent-blog-card-excerpt">
                {getExcerpt(post.content)}
              </p>

              {/* タグ 
              {post.tags && post.tags.length > 0 && (
                <div className="recent-blog-card-tags">
                  {post.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="post-tag-chip"
                      onClick={e => {
                        e.stopPropagation();
                        navigate(`/blog?tag=${encodeURIComponent(tag)}`);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
                
              */}

              {/* 日時 */}
              <small className="recent-blog-card-date">
                {post.updatedAt && !post.updatedAt.isEqual(post.createdAt)
                  ? `最終更新: ${post.updatedAt.toDate().toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                  : `投稿日時: ${post.createdAt.toDate().toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}`}
              </small>
            </article>
          ))
        )}
      </div>

      {/* ブログ一覧へのリンク */}
      {!loading && posts.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            className="recent-blog-more-btn"
            onClick={() => { navigate('/blog'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            記事一覧を見る ≫
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentBlogPosts;
